// src/components/FleetHeader.jsx
//
// Matches the "Fleet Operations" mockup's header exactly: logo mark,
// title (click to reveal the full nav menu - this is the deliberate
// tradeoff requested in place of AdminHeader's always-visible nav
// icons), search bar, live status dot + UTC clock + user email, and
// a user-initials avatar box.
//
// Mobile behavior (breakpoints match fleet-theme.css's own):
//  - <=900px: the real search input is already hidden by fleet-theme.css;
//    a magnifying-glass icon takes its place. Tapping it swaps the icon
//    for the same searchValue/onSearchChange input, focused, and
//    collapses back on outside-click, blur-while-empty, or Escape.
//  - <=600px (fleet-theme.css's own header-wrap breakpoint): the LIVE
//    clock and username text are hidden, leaving just the search icon
//    and the avatar box - both stay on the header's first row (see the
//    .ft-header-right fix in fleet-theme.css).
//
// The logo also swaps to a mobile-specific mark via a <picture> media
// query at the same 600px breakpoint.
//
// Reusable across any fleet-ops-styled admin page (Site Dashboard now,
// Starlink/Monitoring pages next).

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetchJSON } from '../api/apiClient';
import { can } from '../utils/permissions';
import logo from '../images/247Logo.png';
// NOTE: adjust this import if your mobile logo asset has a different filename/path
import logoMobile from '../images/247Logo-mobile.png';
import './tour/ProductTour.css';
import ProductTour from './tour/ProductTour';
import { useActivePageTour, getActiveTourContext } from './tour/tourStore';
import { buildTourSteps, TOURS } from './tour/tours';

// Internal menu, grouped so it stays scannable as pages are added.
// roles: internal roles allowed (admin_role from /api/auth/me); none = all.
const ADMIN_NAV_SECTIONS = [
  {
    title: 'Monitor',
    links: [
      { href: '/admin/dashboard', icon: 'fa-ship', title: 'Site Dashboard' },
      { href: '/admin/sites-map', icon: 'fa-map-marked-alt', title: 'Sites Map' },
      { href: '/admin/alerts', icon: 'fa-bell', title: 'Alert Dashboard' },
      { href: '/admin/starlink-usage', icon: 'fa-satellite-dish', title: 'Starlink Usage' },
    ],
  },
  {
    title: 'Customers',
    links: [
      { href: '/admin/support', icon: 'fa-life-ring', title: 'Support', badge: 'support' },
      { href: '/admin/billing', icon: 'fa-file-invoice-dollar', title: 'Billing' },
      { href: '/admin/client-manager', icon: 'fa-users', title: 'Client Manager' },
    ],
  },
  {
    title: 'Setup',
    links: [
      { href: '/admin/site-manager', icon: 'fa-sitemap', title: 'Site Manager' },
      { href: '/admin/device-manager', icon: 'fa-cog', title: 'Device Manager' },
    ],
  },
  {
    title: 'Admin',
    links: [
      { href: '/admin/admin-manager', icon: 'fa-user-shield', title: 'Admin Manager', roles: ['superadmin'] },
      { href: '/admin/audit-log', icon: 'fa-history', title: 'Audit Log' },
      { href: '/admin/settings', icon: 'fa-sliders-h', title: 'Settings', roles: ['superadmin'] },
    ],
  },
];

// Client menu: short, so no sections. perm: a key from utils/permissions.js,
// set per role by superadmins in Settings > Roles.
const CLIENT_NAV_LINKS = [
  { href: '/dashboard', icon: 'fa-ship', title: 'Site Dashboard', perm: 'fleet.view' },
  { href: '/alerts', icon: 'fa-bell', title: 'Alert Dashboard', perm: 'alerts.view' },
  { href: '/support', icon: 'fa-life-ring', title: 'Support', perm: 'support.use', badge: 'support' },
  { href: '/billing', icon: 'fa-file-invoice-dollar', title: 'Billing', perm: 'billing.view' },
  { href: '/user-manager', icon: 'fa-users', title: 'User Manager', perm: 'users.manage' },
];

function useUtcClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hh = String(now.getUTCHours()).padStart(2, '0');
  const mm = String(now.getUTCMinutes()).padStart(2, '0');
  const ss = String(now.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss} UTC`;
}

// One cheap request per page load: does Support need someone's attention?
// Staff: unread client activity or new tickets (count), plus whether any
// open ticket is urgent (dot). Clients: a ticket waiting on them or a reply
// they haven't opened. Errors just mean no dot.
function useSupportAttention(scope) {
  const [state, setState] = useState({ count: 0, urgent: 0 });
  useEffect(() => {
    let alive = true;
    const url = scope === 'client' ? '/api/v2/client/support/summary' : '/api/v2/admin/support/summary';
    apiFetchJSON(url)
      .then((s) => {
        if (!alive) return;
        setState(scope === 'client'
          ? { count: Math.max(s.waiting || 0, s.unread || 0), urgent: 0 }
          : { count: Math.max(s.new || 0, s.unread || 0), urgent: s.urgent || 0 });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [scope]);
  return state;
}

function getInitials(username) {
  if (!username || typeof username !== 'string') return '';
  return username.slice(0, 2).toUpperCase();
}

// Guided tour: the "?" button appears automatically when the page on
// screen has registered a tour with usePageTour() (see tour/tourStore.js).
// `scope` (admin/client) and the user's role are passed to the tour so
// steps can differ for internal admins vs client users.
export default function FleetHeader({ title, user, onLogout, searchValue, onSearchChange, searchPlaceholder, scope = 'admin' }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const menuRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const clock = useUtcClock();

  // ---- Guided tour ----
  const activeTour = useActivePageTour();
  const hasTour = !!(activeTour.id && TOURS[activeTour.id]);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);
  const tourCtxRef = useRef(null);

  const startTour = () => {
    const ctx = getActiveTourContext() || {};
    const env = {
      scope,
      role: user?.role,
      adminRole: user?.admin_role,
      user,
      isMobile: ctx.isMobile ?? window.innerWidth <= 768,
    };
    setMenuOpen(false);
    tourCtxRef.current = ctx;
    ctx.onTourStart && ctx.onTourStart();
    setTourSteps(buildTourSteps(activeTour.id, ctx, env));
    setTourOpen(true);
  };

  const endTour = () => {
    setTourOpen(false);
    tourCtxRef.current?.onTourEnd && tourCtxRef.current.onTourEnd();
    tourCtxRef.current = null;
  };

  const { count: supportAttention, urgent: supportUrgent } = useSupportAttention(scope);
  const currentRole = user?.admin_role || user?.role;
  const navSections = scope === 'client'
    ? [{ title: null, links: CLIENT_NAV_LINKS.filter((link) => can(user, link.perm)) }]
    : ADMIN_NAV_SECTIONS
        .map((sec) => ({ ...sec, links: sec.links.filter((link) => !link.roles || link.roles.includes(currentRole)) }))
        .filter((sec) => sec.links.length);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(e.target)) {
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const openMobileSearch = () => {
    setMobileSearchOpen(true);
    // input isn't mounted yet on this same tick, so focus on next paint
    requestAnimationFrame(() => mobileSearchInputRef.current?.focus());
  };

  const handleMobileSearchKeyDown = (e) => {
    if (e.key === 'Escape') setMobileSearchOpen(false);
  };

  const handleMobileSearchBlur = () => {
    if (!searchValue) setMobileSearchOpen(false);
  };

  return (
    <div
      className="ft-header"
      style={{
        // Same fix as the main CRM header: without this, the native
        // Capacitor-wrapped app (WKWebView renders edge-to-edge, no
        // browser chrome to push content down) draws this bar under/
        // behind the iPhone notch or Dynamic Island. env(...) only
        // resolves to a nonzero value once index.html's viewport meta
        // tag has viewport-fit=cover; in a normal browser tab (or if
        // that meta tag is missing) it's 0px and this adds nothing.
        // Capped at 50px in case the native WebView container is also
        // applying its own safe-area inset on top of this.
        paddingTop: "min(env(safe-area-inset-top, 0px), 50px)",
        boxSizing: "content-box",
      }}
    >
      <picture>
        <source media="(max-width: 600px)" srcSet={logoMobile} />
        <img src={logo} alt="" className="ft-header-logo" />
      </picture>

      <div className="ft-header-menu-wrap" ref={menuRef}>
        <button type="button" className="ft-header-title" onClick={() => setMenuOpen((o) => !o)}>
          {title}
          {(supportAttention > 0 || supportUrgent > 0) && !menuOpen && (
            <span className="ft-header-attention" aria-label="Support needs attention" />
          )}
          <i className={`fas fa-chevron-${menuOpen ? 'up' : 'down'} ft-header-title-caret`}></i>
        </button>

        {menuOpen && (
          <div className={`ft-header-menu ${navSections.length > 1 ? 'is-sectioned' : ''}`}>
            <div className="ft-header-menu-sections">
              {navSections.map((sec) => (
                <div key={sec.title || 'main'} className="ft-header-menu-section" role="group" aria-label={sec.title || undefined}>
                  {sec.title && <div className="ft-header-menu-heading">{sec.title}</div>}
                  {sec.links.map((link) => (
                    <button
                      type="button"
                      key={link.href}
                      className={`ft-header-menu-item ${currentPath === link.href ? 'is-current' : ''}`}
                      aria-current={currentPath === link.href ? 'page' : undefined}
                      onClick={() => { setMenuOpen(false); navigate(link.href); }}
                    >
                      <i className={`fas ${link.icon}`}></i>
                      {link.title}
                      {link.badge === 'support' && supportUrgent > 0 && (
                        <span
                          className="ft-header-menu-dot"
                          title={`${supportUrgent} urgent open ${supportUrgent === 1 ? 'ticket' : 'tickets'}`}
                          aria-label={`${supportUrgent} urgent open ${supportUrgent === 1 ? 'ticket' : 'tickets'}`}
                        />
                      )}
                      {link.badge === 'support' && supportAttention > 0 && (
                        <span className={`ft-header-menu-count ${supportUrgent > 0 ? 'has-dot' : ''}`}>{supportAttention}</span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <div className="ft-header-menu-sep" />
            <button
              type="button"
              className="ft-header-menu-item"
              onClick={() => { setMenuOpen(false); navigate('/security'); }}
            >
              <i className="fas fa-shield-alt"></i>
              Security
            </button>
            <button
              type="button"
              className="ft-header-menu-item"
              onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
            >
              <i className="fas fa-sign-out-alt"></i>
              Logout
            </button>
          </div>
        )}
      </div>

      {onSearchChange && (
        <input
          data-tour="header-search"
          className="ft-header-search"
          placeholder={searchPlaceholder || 'search'}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      )}

      <div className="ft-header-right">
        <span className="ft-header-live-clock"><span className="ft-live-dot"></span>LIVE · {clock}</span>
        {user?.username && <span className="ft-header-username">{user.username}</span>}

        {onSearchChange && (
          <div className="ft-header-mobile-search" ref={mobileSearchRef}>
            {mobileSearchOpen ? (
              <input
                ref={mobileSearchInputRef}
                className="ft-header-mobile-search-input"
                placeholder={searchPlaceholder || 'search'}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={handleMobileSearchKeyDown}
                onBlur={handleMobileSearchBlur}
              />
            ) : (
              <button
                type="button"
                data-tour="header-search"
                className="ft-header-search-icon-btn"
                onClick={openMobileSearch}
                aria-label="Search"
              >
                <i className="fas fa-search"></i>
              </button>
            )}
          </div>
        )}

        {hasTour && (
          <button
            type="button"
            className="ft-header-help-btn"
            onClick={startTour}
            disabled={!activeTour.ready || tourOpen}
            aria-label="Show page tutorial"
            title="Show page tutorial"
            data-tour="tour-help"
          >
            ?
          </button>
        )}

        {user?.username && (
          <span className="ft-header-avatar-box">{getInitials(user.username)}</span>
        )}
      </div>

      <ProductTour open={tourOpen} steps={tourSteps} onClose={endTour} />
    </div>
  );
}
